import React, { useEffect, useState } from 'react';
import './Toast.css';

/**
 * Toast notification component
 * 
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {string} props.type - The type of toast ('success', 'error', 'info')
 * @param {boolean} props.visible - Whether the toast is visible
 * @param {function} props.onClose - Function to call when the toast should close
 * @param {number} props.duration - Duration in milliseconds before auto-closing (default: 2000)
 * @param {Object} props.timersRef - React ref object to store timer references
 */
const Toast = ({ 
  message, 
  type = 'success', 
  visible, 
  onClose,
  duration = 2000,
  timersRef
}) => {
  const [isExiting, setIsExiting] = useState(false);
  
  useEffect(() => {
    if (visible) {
      setIsExiting(false); // Reset exit state when a new toast shows
      
      // Set a timeout for the exit animation
      timersRef.current.animation = setTimeout(() => {
        setIsExiting(true);
      }, duration - 300); // Start exit animation 300ms before closing
      
      // Set a timeout for the actual close
      timersRef.current.close = setTimeout(() => {
        setIsExiting(false); // Reset for next time
        onClose();
      }, duration);
      
      return () => {
        // Clear timers on unmount or when visible changes
        if (timersRef.current.animation) {
          clearTimeout(timersRef.current.animation);
        }
        if (timersRef.current.close) {
          clearTimeout(timersRef.current.close);
        }
      };
    }
  }, [visible, onClose, duration, timersRef, message]); // Include message to reset timers when it changes

  if (!visible) return null;

  return (
    <div className={`toast toast--${type} ${isExiting ? 'toast--exiting' : 'toast--visible'}`}>
      <div className="toast__content">
        {message}
      </div>
    </div>
  );
};

export default Toast;